import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyleKit } from '@tiptap/extension-text-style'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link2,
  ChevronDown, Palette, RemoveFormatting,
} from 'lucide-react'
import {
  TEXT_COLORS, TEXT_SIZES, asHexOrDefault, currentColor, currentSizeLabel,
} from './rich-text-styling'
import { promptLink } from './rich-text-link'

/**
 * Éditeur rich-text notion-like.
 * - Monté DANS l'élément du champ (portail DOM) : WYSIWYG, la surface
 *   d'édition hérite de la cascade du champ — aucune classe typographique
 *   propre à l'éditeur.
 * - Menu flottant custom qui apparaît à la sélection de texte.
 * - Applique les modifications au clic dehors ; Escape = annuler.
 *
 * Quand le champ déclare `formats:` dans son def, l'éditeur émet
 * `{format, value}`. L'inline n'expose pas de sélecteur de format —
 * il utilise `defaultFormat` (ou le premier format autorisé). Pour
 * changer de format, l'utilisateur ouvre le modale `RichTextEditor`.
 */
export const InlineRichTextEditor = ({ initialValue, formats, defaultFormat, onSave, onClose }) => {
  const containerRef = useRef(null)
  const [selectionRect, setSelectionRect] = useState(null)

  const hasFormats = Array.isArray(formats) && formats.length > 0
  // Normalize incoming value : either a plain string (legacy) or `{format, value}`.
  const incomingRaw = (initialValue && typeof initialValue === 'object' && 'value' in initialValue)
    ? (initialValue.value ?? '')
    : (typeof initialValue === 'string' ? initialValue : '')
  const incomingFormat = (initialValue && typeof initialValue === 'object' && initialValue.format)
    || defaultFormat
    || (formats?.[0] ?? null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({ openOnClick: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // Taille et couleur : deux attributs de la marque `textStyle`. Le reste
      // du kit reste éteint — famille de police et interligne appartiennent au
      // bloc (ses classes du design system), pas au mot sélectionné.
      TextStyleKit.configure({
        fontSize: { types: ['textStyle'] },
        color: { types: ['textStyle'] },
        fontFamily: false,
        lineHeight: false,
        backgroundColor: false,
      }),
    ],
    content: incomingRaw,
    autofocus: 'end',
  })

  const emitSave = useCallback((html) => {
    if (hasFormats) {
      onSave({ format: incomingFormat, value: html })
    } else {
      onSave(html)
    }
  }, [hasFormats, incomingFormat, onSave])

  // Mise à jour de la position du menu flottant à chaque changement de sélection
  const updateSelectionRect = useCallback(() => {
    if (!editor) return
    const { selection } = editor.state
    if (selection.empty) {
      setSelectionRect(null)
      return
    }
    const domSel = window.getSelection()
    if (!domSel || domSel.rangeCount === 0) { setSelectionRect(null); return }
    const rect = domSel.getRangeAt(0).getBoundingClientRect()
    if (!rect || rect.width === 0) { setSelectionRect(null); return }
    setSelectionRect(rect)
  }, [editor])

  useEffect(() => {
    if (!editor) return
    editor.on('selectionUpdate', updateSelectionRect)
    editor.on('transaction', updateSelectionRect)
    return () => {
      editor.off('selectionUpdate', updateSelectionRect)
      editor.off('transaction', updateSelectionRect)
    }
  }, [editor, updateSelectionRect])

  // Applique les modifications au clic en dehors de l'éditeur et du menu flottant
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!containerRef.current) return
      // Un item d'un menu de la bulle démonte son panneau DANS le handler React
      // du clic : quand ce listener s'exécute (sur document, en fin de remontée),
      // `e.target` n'est plus rattaché au document et `closest()` ne
      // retrouve plus la bulle — le clic passerait pour « dehors » et refermerait
      // l'édition. La bulle marque donc l'événement natif à la descente.
      if (e[BUBBLE_MOUSEDOWN]) return
      if (containerRef.current.contains(e.target)) return
      if (e.target.closest('[data-builder-bubble]')) return
      if (editor) emitSave(editor.getHTML())
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [editor, emitSave])

  // Escape → fermer sans appliquer
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  if (!editor) return null

  return (
    <>
      <div ref={containerRef} className="builder-richtxt-inline">
        {/* Aucune classe typographique ici (pas de `prose`) : la surface
            d'édition doit hériter de la cascade du champ hôte — sinon la
            taille, la couleur et les marges changent à l'ouverture. */}
        <EditorContent editor={editor} className="builder-richtxt-content" />
      </div>

      {selectionRect && createPortal(
        <FloatingMenu editor={editor} rect={selectionRect} />,
        document.body
      )}
    </>
  )
}

/* ── Menu flottant (portail) ─────────────────────────────── */

/** Marque posée sur l'événement natif par la bulle (cf. handleMouseDown). */
const BUBBLE_MOUSEDOWN = '__builderBubbleMouseDown'

const FloatingMenu = ({ editor, rect }) => {
  const top = rect.top - 48
  const left = rect.left + rect.width / 2

  return (
    <div
      data-builder-bubble=""
      className="builder-bubble-menu"
      onMouseDownCapture={(e) => { e.nativeEvent[BUBBLE_MOUSEDOWN] = true }}
      style={{ position: 'fixed', top, left, transform: 'translateX(-50%)', zIndex: 9999 }}
    >
      <BubbleHeadingBtn editor={editor} level={0} label="P" />
      <BubbleHeadingBtn editor={editor} level={2} label="H2" />
      <BubbleHeadingBtn editor={editor} level={3} label="H3" />
      <BubbleHeadingBtn editor={editor} level={4} label="H4" />

      <BubbleSep />

      <BubbleBtn editor={editor} action="toggleBold" isActive={editor.isActive('bold')} label="Bold"><Bold size={13} /></BubbleBtn>
      <BubbleBtn editor={editor} action="toggleItalic" isActive={editor.isActive('italic')} label="Italic"><Italic size={13} /></BubbleBtn>
      <BubbleBtn editor={editor} action="toggleUnderline" isActive={editor.isActive('underline')} label="Underline"><UnderlineIcon size={13} /></BubbleBtn>
      <BubbleBtn editor={editor} action="toggleStrike" isActive={editor.isActive('strike')} label="Strikethrough"><Strikethrough size={13} /></BubbleBtn>

      <BubbleSep />

      <BubbleSizeMenu editor={editor} />
      <BubbleColorMenu editor={editor} />

      <BubbleSep />

      <BubbleAlignBtn editor={editor} align="left" label="Align left"><AlignLeft size={13} /></BubbleAlignBtn>
      <BubbleAlignBtn editor={editor} align="center" label="Center"><AlignCenter size={13} /></BubbleAlignBtn>
      <BubbleAlignBtn editor={editor} align="right" label="Align right"><AlignRight size={13} /></BubbleAlignBtn>

      <BubbleSep />

      <BubbleBtn editor={editor} action="toggleBulletList" isActive={editor.isActive('bulletList')} label="Bullet list"><List size={13} /></BubbleBtn>
      <BubbleBtn editor={editor} action="toggleOrderedList" isActive={editor.isActive('orderedList')} label="Ordered list"><ListOrdered size={13} /></BubbleBtn>

      <BubbleSep />

      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault()
          promptLink(editor)
        }}
        className={`builder-bubble-btn ${editor.isActive('link') ? 'builder-bubble-btn-active' : ''}`}
        title="Link"
        aria-label="Link"
      >
        <Link2 size={13} />
      </button>
    </div>
  )
}

/* ── Sous-composants ─────────────────────────────────────── */

const BubbleSep = () => <div className="builder-bubble-sep" />

/**
 * Menu déroulant de la bulle (taille, couleur).
 *
 * `children` est une fonction qui reçoit `close` : c'est l'item qui décide
 * quand refermer — le sélecteur de couleur libre, lui, doit rester monté
 * pendant que la palette native de l'OS est ouverte.
 */
const BubbleDropdown = ({ label, trigger, isActive, children }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    // Échap referme le panneau, PAS l'édition : l'éditeur écoute `keydown` sur
    // document en phase de bulle, on l'intercepte donc en CAPTURE et on stoppe
    // la propagation — sinon un Échap sur le panneau annulerait toute la saisie.
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open])

  return (
    <div ref={ref} className="builder-bubble-dropdown-wrap">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(v => !v) }}
        className={`builder-bubble-btn ${isActive || open ? 'builder-bubble-btn-active' : ''}`}
        aria-label={label}
        title={label}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && (
        <div className="builder-bubble-dropdown">
          {children(close)}
        </div>
      )}
    </div>
  )
}

/** Taille du texte — en multiples de la taille héritée du champ. */
const BubbleSizeMenu = ({ editor }) => {
  const current = editor.getAttributes('textStyle').fontSize || null

  return (
    <BubbleDropdown
      label="Taille du texte"
      isActive={!!current}
      trigger={
        <>
          <span className="builder-bubble-dropdown-value">{currentSizeLabel(editor)}</span>
          <ChevronDown size={10} aria-hidden="true" />
        </>
      }
    >
      {(close) => (
        <>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().unsetFontSize().run()
              close()
            }}
            className={`builder-bubble-dropitem ${current ? '' : 'builder-bubble-dropitem-active'}`}
          >
            <RemoveFormatting size={12} aria-hidden="true" />
            Taille du bloc
          </button>
          <div className="builder-bubble-dropdivider" />
          {TEXT_SIZES.map(size => (
            <button
              key={size.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setFontSize(size.value).run()
                close()
              }}
              className={`builder-bubble-dropitem ${current === size.value ? 'builder-bubble-dropitem-active' : ''}`}
            >
              <span className="builder-bubble-size-preview" style={{ fontSize: size.value }} aria-hidden="true">Aa</span>
              {size.label}
            </button>
          ))}
        </>
      )}
    </BubbleDropdown>
  )
}

/** Couleur du texte — jetons du design system, plus une couleur libre. */
const BubbleColorMenu = ({ editor }) => {
  const current = currentColor(editor)

  return (
    <BubbleDropdown
      label="Couleur du texte"
      isActive={!!current}
      trigger={
        <>
          <Palette size={13} aria-hidden="true" />
          <ChevronDown size={10} aria-hidden="true" />
        </>
      }
    >
      {(close) => (
        <>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().unsetColor().run()
              close()
            }}
            className={`builder-bubble-dropitem ${current ? '' : 'builder-bubble-dropitem-active'}`}
          >
            <RemoveFormatting size={12} aria-hidden="true" />
            Couleur du bloc
          </button>
          <div className="builder-bubble-dropdivider" />
          {TEXT_COLORS.map(color => (
            <button
              key={color.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setColor(color.value).run()
                close()
              }}
              className={`builder-bubble-dropitem ${current === color.value ? 'builder-bubble-dropitem-active' : ''}`}
            >
              <span className="builder-bubble-swatch" style={{ background: color.value }} aria-hidden="true" />
              {color.label}
            </button>
          ))}
          <div className="builder-bubble-dropdivider" />
          <label className="builder-bubble-dropitem">
            <span
              className="builder-bubble-swatch"
              style={{ background: current || 'transparent' }}
              aria-hidden="true"
            />
            Personnalisée
            <input
              type="color"
              className="builder-bubble-color-input"
              value={asHexOrDefault(current)}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              aria-label="Choisir une couleur personnalisée"
            />
          </label>
        </>
      )}
    </BubbleDropdown>
  )
}

const BubbleBtn = ({ editor, action, isActive, label, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus()[action]().run() }}
    className={`builder-bubble-btn ${isActive ? 'builder-bubble-btn-active' : ''}`}
    aria-label={label}
    title={label}
  >
    {children}
  </button>
)

const BubbleAlignBtn = ({ editor, align, label, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign(align).run() }}
    className={`builder-bubble-btn ${editor.isActive({ textAlign: align }) ? 'builder-bubble-btn-active' : ''}`}
    aria-label={label}
    title={label}
  >
    {children}
  </button>
)

const BubbleHeadingBtn = ({ editor, level, label }) => {
  const isActive = level === 0
    ? !editor.isActive('heading')
    : editor.isActive('heading', { level })

  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault()
        if (level === 0) editor.chain().focus().setParagraph().run()
        else editor.chain().focus().setHeading({ level }).run()
      }}
      className={`builder-bubble-btn builder-bubble-heading-btn ${isActive ? 'builder-bubble-btn-active' : ''}`}
      aria-label={label}
      title={label}
    >
      {label}
    </button>
  )
}
